// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: InjectableCss;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: InjectableCss;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    linkList1GridItem: {
        css: css`
            width: 20%;
            @media (min-width: 769px) and (max-width: 1024px) {
                width: 15%;
            }
            @media (min-width: 600px) and (max-width: 769px) {
                width: 33% !important;
            }
            &.social-wrapper {
                width: 15%;
                margin-right: 40px !important;
                svg {
                    fill: #fff;
                }
                span,
                p {
                    color: #fff !important;
                }
                @media (min-width: 600px) and (max-width: 769px) {
                    width: 45% !important;
                }
            }
            &&& a {
                span {
                    text-transform: capitalize;
                    font-weight: 400;
                }
            }

            &.subscribe-wrapper {
                width: 25%;
                max-width: 443px;

                @media (min-width: 769px) and (max-width: 1024px) {
                    width: 30%;
                }
                @media (min-width: 600px) and (max-width: 769px) {
                    width: 45% !important;
                }
            }

            &&:not(:last-child) {
                margin-right: 10px;

                @media (width: 1024px) {
                    margin-right: 10px;
                }
            }

            @media (max-width: 1023px) and (min-width: 769px) {
                width: calc(100% / 3 - 25px);
                margin-bottom: 20px;
                &&:not(:last-child) {
                    margin-right: 25px;
                }
            }

            &&& {
                @media (max-width: 768px) {
                    width: 100%;
                    margin-right: 0;

                    &&:not(:first-child) {
                        margin-top: 5px;
                    }
                }
            }

            .section-title {
                margin-bottom: 6px;
                display: block;

                &,
                > span {
                    text-transform: capitalize;
                    font-size: 17px;
                    letter-spacing: 0.2px;
                    font-weight: 700 !important;
                }
            }

            [class*="StyledButton"] {
                text-transform: capitalize;
                color: ${getColor("common.white")};
                &:hover,
                &:focus {
                    text-decoration: underline;
                }
            }
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));

    return (
        <StyledWrapper
            css={css`
                display: flex;
                padding-top: 30px;
                padding-bottom: 15px;
                @media (max-width: 1023px) {
                    flex-wrap: wrap;
                }
            `}
        >
            <StyledWrapper {...styles.linkList1GridItem}>
                <Zone zoneName="LinkList1" contentId={id} />
            </StyledWrapper>
            <StyledWrapper {...styles.linkList1GridItem}>
                <Zone zoneName="LinkList2" contentId={id} />
            </StyledWrapper>
            <StyledWrapper {...styles.linkList1GridItem}>
                <Zone zoneName="LinkList3" contentId={id} />
            </StyledWrapper>
            <StyledWrapper {...styles.linkList1GridItem} className="social-wrapper">
                <Zone zoneName="LinkList4" contentId={id} />
            </StyledWrapper>
            <StyledWrapper {...styles.linkList1GridItem} className="subscribe-wrapper">
                <Zone zoneName="LinkList5" contentId={id} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
